import {
  Co6TransactionTypes,
  RegularISubmit,
  TaxDocumentation,
  TaxDocumentationStatus,
  TermsAcceptance,
} from '@interfaces';
import {
  FlowTypes,
  IRATypes,
  IReceipt,
  IReceiptDTO,
  ISelectedClientAccountsState,
  YesNoEmpty,
} from '@interfaces/models';
import { VgEmploymentState } from '@interfaces/models/open-account-mfes/employment-mfe.model';
import { OneTimePasscodeStatus } from '@interfaces/one-time-passcode/otp.interface';
import {
  AccountTriageState,
  DecedentInfoState,
  FeatureToggleState,
  IOpenAccountState,
  TriageState,
} from '@interfaces/state';
import { RelationshipDesignationTypes, stateToFormNameMap } from '@shared/models';
import { TransactionState } from '@store/reducers';
import { EstateState } from '@store/reducers/estate/estate.reducer';
import { MultiAccordionsMap } from '@store/reducers/multi-terms-accordion/multi-terms-accordion.reducer';
import {
  getClientName,
  PersonalDataState,
} from '@store/reducers/personal-data/personal-data.reducer';
import { MfePersonalAddressState } from '@store/reducers/personal-info-address/personal-info-address.reducers';
import { MfePhoneState } from '@store/reducers/personal-phone/personal-phone.reducer';
import { ITaxDocumentUploadState } from '@store/reducers/tax-document-upload/tax-document-upload.reducer';
import {
  getIsExecutor,
  getIsInheritanceFlow,
  getIsIRAAccount,
} from '@store/reducers/triage/triage.reducer';
import { TrustTaxInfoState } from '@store/reducers/trust-tax-info/trust-tax-info.reducer';

import { MultiInheritanceBackupWithholdingSectionPayload } from '../../../../../../interfaces/submit/submit.service.interface';
import { isTaxDocumentationRequired } from '../tax-document-upload/tax-document-upload.helpers';

/**
 * Mapping of checkbox keys to their corresponding TaxDocumentationStatus values.
 * Add new checkbox mappings here to automatically support them in convertTaxDocumentationStateToStatus.
 */
const checkboxToStatusMap: Record<string, TaxDocumentationStatus> = {
  mailingForm: 'mailingForm',
  beneficiarySubmitted: 'beneficiarySubmitted',
  noFormNeeded: 'noFormNeeded',
};

/**
 * Checks if any checkbox in the selections is true.
 * @param checkboxSelections - The checkbox selections object
 * @returns true if at least one checkbox is selected
 */
export function hasAnyCheckboxSelected(
  checkboxSelections: ITaxDocumentUploadState['checkboxSelections'],
): boolean {
  return Object.values(checkboxSelections).some(Boolean);
}

/**
 * Gets the selected checkboxes from the selections object.
 * @param checkboxSelections - The checkbox selections object
 * @returns Array of keys for selected checkboxes
 */
export function getSelectedCheckboxKeys(
  checkboxSelections: ITaxDocumentUploadState['checkboxSelections'],
): string[] {
  return Object.entries(checkboxSelections)
    .filter(([, value]) => value)
    .map(([key]) => key);
}

export function checkForFlowTypes(
  flowType: FlowTypes,
):
  | Co6TransactionTypes.gifting
  | Co6TransactionTypes.inheritance
  | Co6TransactionTypes.minor
  | Co6TransactionTypes.inheritedIra {
  switch (flowType) {
    case FlowTypes.GIFTING:
      return Co6TransactionTypes.gifting;
    case FlowTypes.INHERITANCE:
      return Co6TransactionTypes.inheritance;
    case FlowTypes.MINOR:
      return Co6TransactionTypes.minor;
    case FlowTypes.INHERITED_IRA:
      return Co6TransactionTypes.inheritedIra;
    default:
      return;
  }
}

export function createReceiptDTO(
  triageState: TriageState,
  selectedClientAccountsState: ISelectedClientAccountsState,
  receiptDTO: IReceiptDTO | any,
): IReceiptDTO | any {
  const iraType = selectedClientAccountsState.transferTo?.iraType;
  const isNonEstateIRAInheritance =
    getIsInheritanceFlow(triageState) &&
    getIsIRAAccount(triageState) &&
    !getIsExecutor(triageState);
  const isInheritedIRAAccountType =
    iraType === IRATypes.INHERITED_OTHER || iraType === IRATypes.INHERITED_ROTH;

  if (isNonEstateIRAInheritance && isInheritedIRAAccountType) {
    const comment = 'Review of BENEF line required to match original decedent';
    return {
      ...receiptDTO,
      to: {
        ...receiptDTO.to,
        rows: receiptDTO.to.rows.map((row: any) =>
          row.evidenceID === 'toAccountNumber'
            ? { ...row, values: [...row.values, comment] }
            : { ...row },
        ),
      },
    };
  }

  return receiptDTO;
}

/**
 * Converts the frontend tax documentation checkbox selections to backend status string.
 *
 * Rules:
 * - If file is uploaded and no checkboxes are selected → 'taxFileUploaded'
 * - If exactly one checkbox is true → return that checkbox's status
 * - If more than one checkbox is true → 'notValid'
 */
export function convertTaxDocumentationStateToStatus(
  checkboxSelections: ITaxDocumentUploadState['checkboxSelections'],
  taxFileUploaded: boolean,
): TaxDocumentationStatus | undefined {
  const anyCheckboxSelected = hasAnyCheckboxSelected(checkboxSelections);

  if (taxFileUploaded && !anyCheckboxSelected) {
    return 'taxFileUploaded';
  }

  const selectedKeys = getSelectedCheckboxKeys(checkboxSelections);

  if (selectedKeys.length > 1) {
    return 'notValid';
  }

  if (selectedKeys.length === 1) {
    return checkboxToStatusMap[selectedKeys[0]];
  }

  return undefined;
}

export function getTaxDocumentationConfig(
  featureToggles: FeatureToggleState,
  checkboxSelections: ITaxDocumentUploadState['checkboxSelections'],
  taxFileUploaded: boolean,
  inheritanceWaiverState: string,
  fromAccount: AccountTriageState,
): { taxDocumentation: TaxDocumentation } | undefined {
  if (
    !featureToggles.l8_itw_upload ||
    !isTaxDocumentationRequired(inheritanceWaiverState, fromAccount)
  ) {
    return undefined;
  }

  const status = convertTaxDocumentationStateToStatus(checkboxSelections, taxFileUploaded);
  const formName = status ? stateToFormNameMap[inheritanceWaiverState] : undefined;

  return {
    taxDocumentation: {
      status,
      formName,
    },
  };
}

export const createMultiSubmitPayload = (
  clientPersonalInfoData: {
    firstName: string;
    middleName: string;
    lastName: string;
    email: string;
    birthDate: string;
    citizenshipStatus: string;
    gender: string;
    countryOfCitizenship: string;
    suffix: string;
  },
  personalData: PersonalDataState,
  decedentInfo: DecedentInfoState,
  featureToggles: FeatureToggleState,
  triageState: TriageState,
  openAccountState: IOpenAccountState,
  otpVerificationStatus: OneTimePasscodeStatus,
  employmentFeature: Partial<VgEmploymentState> | undefined,
  address: MfePersonalAddressState,
  phone: MfePhoneState,
  transactions: TransactionState[],
  receiptMulti: { [key: string]: IReceipt | IReceipt[] | null },
  taxDocumentationCheckBoxSelections: ITaxDocumentUploadState['checkboxSelections'],
  taxFileUploaded: boolean,
  multiAccordions?: MultiAccordionsMap,
  multiInheritanceBackupWithholdingSections?: MultiInheritanceBackupWithholdingSectionPayload,
): RegularISubmit[] | null => {
  if (!featureToggles.cwp_multi_inheritance || transactions.length === 0) {
    return null;
  }
  if (!receiptMulti) {
    throw new Error(`createMultiSubmitPayload: receiptMulti is ${receiptMulti}`);
  }
  const transactionType: Co6TransactionTypes.inheritance = Co6TransactionTypes.inheritance;
  const hideWithholding = featureToggles.cwp_inheritance_new_terms;
  const termsAcceptance = buildTermsAcceptance(multiAccordions);

  return transactions.map((transaction, index) => {
    const triageStateNew: TriageState & {
      isSingleExecutor: YesNoEmpty;
      isSingleTrustee: YesNoEmpty;
      isDecedentMarried: YesNoEmpty;
    } = {
      ...triageState,
      ...transaction.accountTriage,
      isSingleExecutor: transaction.accountTriage.isOnlyExecutor || YesNoEmpty.EMPTY,
      isSingleTrustee: transaction.accountTriage.isOnlyTrustee || YesNoEmpty.EMPTY,
      isDecedentMarried: transaction.accountTriage.wasMarried || YesNoEmpty.EMPTY,
      flowType: FlowTypes.MULTI_INHERITANCE,
    };
    const fromAccountIndex = (receiptMulti.transfer as IReceipt[])?.findIndex(
      (transfer) => transfer.header === `Transfer ${index + 1}`,
    );
    const receiptDTONew: any = {
      ...receiptMulti,
      gift: null,
      additionalTaxLots: null,
      to: (receiptMulti.to as IReceipt[])?.[index],
      from: (receiptMulti.transfer as IReceipt[])?.[fromAccountIndex],
      beneByRelationship:
        (receiptMulti.transfer as IReceipt[])?.[fromAccountIndex + 1]?.header ===
        'Beneficiary designation information'
          ? (receiptMulti.transfer as IReceipt[])[fromAccountIndex + 1]
          : null,
    };
    return {
      config: {
        personalData: {
          ownerName: getClientName(clientPersonalInfoData),
          segment: personalData.segment,
          dateOfBirth: clientPersonalInfoData.birthDate,
          citizenshipStatus: clientPersonalInfoData.citizenshipStatus,
          countryOfCitizenship: clientPersonalInfoData.countryOfCitizenship,
        },
        decedentInfo: decedentInfo,
        featureToggles: featureToggles,
        triage: triageStateNew,
        trust: transaction?.trust,
        trustTaxInfo: hideWithholding
          ? ((({ withholdingQuestion, signature, date, ...rest }) => rest)(
              transaction?.trustTaxInfo ?? ({} as TrustTaxInfoState),
            ) as typeof transaction.trustTaxInfo)
          : transaction?.trustTaxInfo,
        estateInfo: hideWithholding
          ? ((({ withholdingQuestion, signature, date, ...rest }) => rest)(
              transaction?.estate ?? ({} as EstateState),
            ) as typeof transaction.estate)
          : transaction?.estate,
        openAccountState: openAccountState,
        selectedAccs: transaction.selectedClientAccounts,
        employment:
          employmentFeature?.associations && employmentFeature?.employmentStatus
            ? {
                associations: employmentFeature.associations,
                employmentStatus: employmentFeature.employmentStatus,
              }
            : undefined,
        mailingAddress: address.personalAddress?.mailingAddress,
        streetAddress: address.personalAddress?.streetAddress,
        phone,
        beneficiaries: {
          relationshipDesignation:
            transaction.accountTriage.isOnlyChild || transaction.beneficiaries?.length
              ? RelationshipDesignationTypes.DESCENDANT
              : RelationshipDesignationTypes.UNKNOWN,
          onlyChild: !!transaction.accountTriage.isOnlyChild,
          otherBenes: transaction.beneficiaries,
        },
        investmentObjectives: transaction.investmentObjectives,
        termsAcceptance,
        backupWithholdingSignatureName: transaction.backupWithholdingSignatureName,
        backupWithholdingAnswer: transaction.backupWithholdingAnswer,
        ...getTaxDocumentationConfig(
          featureToggles,
          taxDocumentationCheckBoxSelections,
          taxFileUploaded,
          decedentInfo.inheritanceWaiverState,
          transaction.accountTriage,
        ),
      },
      receiptDTO: createReceiptDTO(
        triageStateNew,
        transaction.selectedClientAccounts,
        receiptDTONew,
      ),
      transactionType: transactionType,
      otpVerificationStatus: otpVerificationStatus,
      multiInheritanceBackupWithholdingSections,
    };
  }) as RegularISubmit[];
};

export function buildTermsAcceptance(
  multiAccordions?: MultiAccordionsMap | null,
): TermsAcceptance | undefined {
  if (!multiAccordions) return undefined;
  return Object.keys(multiAccordions).reduce(
    (acc, key) => {
      acc[key] = multiAccordions[key]?.acceptedAt ?? null;
      return acc;
    },
    {} as Record<string, string | null>,
  );
}

export function buildT2TTermsReceipt(
  backupWithholdingAnswer: 'yes' | 'no' | undefined,
  backupWithholdingSignatureName: string | undefined,
  termsAcceptance: TermsAcceptance | undefined,
): IReceipt | undefined {
  const hasData = backupWithholdingAnswer || backupWithholdingSignatureName || termsAcceptance;
  if (!hasData) return undefined;

  const rows: IReceipt['rows'] = [];

  if (termsAcceptance) {
    const namedFields: Array<{ key: keyof TermsAcceptance; label: string; evidenceID: string }> = [
      {
        key: 'documentsAndElectronicConsent',
        label: 'Documents And Electronic Consent',
        evidenceID: 'documentsAndElectronicConsent',
      },
      {
        key: 'backupWithholdingIra',
        label: 'Backup Withholding IRA',
        evidenceID: 'backupWithholdingIra',
      },
      {
        key: 'backupWithholdingIndividualJoint',
        label: 'Backup Withholding Individual/Joint',
        evidenceID: 'backupWithholdingIndividualJoint',
      },
      {
        key: 'backupWithholdingTrustW8',
        label: 'Backup Withholding W8',
        evidenceID: 'backupWithholdingTrustW8',
      },
      {
        key: 'spouseCertification',
        label: 'Spouse Certification',
        evidenceID: 'spouseCertification',
      },
      {
        key: 'beneficiaryCertification',
        label: 'Beneficiary Certification',
        evidenceID: 'beneficiaryCertification',
      },
      {
        key: 'trusteeCertification',
        label: 'Trustee Certification',
        evidenceID: 'trusteeCertification',
      },
      { key: 'signApplication', label: 'Sign Application', evidenceID: 'signApplication' },
    ];
    namedFields.forEach(({ key, label, evidenceID }) => {
      const value = termsAcceptance[key];
      if (value) rows.push({ label, values: [value as string], evidenceID });
    });
  }

  if (backupWithholdingAnswer) {
    rows.push({
      label: 'Subject To Backup Withholding',
      values: [backupWithholdingAnswer === 'yes' ? 'Yes' : 'No'],
      evidenceID: 'backupWithholding',
    });
  }

  if (backupWithholdingSignatureName) {
    rows.push({
      label: 'Signature',
      values: [backupWithholdingSignatureName],
      evidenceID: 'signature',
    });
  }

  if (rows.length === 0) return undefined;

  return { header: 'Terms', rows, editable: false, sectionEvidenceID: 'terms' };
}
